/**
 * Milestone Thresholds Configuration
 * Story 9.2: Milestone Celebration Notifications
 *
 * Defines the milestone thresholds that trigger celebrations.
 */

import type { MilestoneType } from '../types';

export interface MilestoneThreshold {
  type: 'questions' | 'feedback';
  value: number;
  label: string;
}

/**
 * Milestone threshold configurations
 * Maps milestone type to the metric type and value needed to achieve it
 */
export const MILESTONE_THRESHOLDS: Record<MilestoneType, MilestoneThreshold> = {
  first_question: {
    type: 'questions',
    value: 1,
    label: 'First Question Answered',
  },
  questions_100: {
    type: 'questions',
    value: 100,
    label: '100 Questions Answered',
  },
  questions_500: {
    type: 'questions',
    value: 500,
    label: '500 Questions Answered',
  },
  questions_1000: {
    type: 'questions',
    value: 1000,
    label: '1,000 Questions Answered',
  },
  first_positive_feedback: {
    type: 'feedback',
    value: 1,
    label: 'First Positive Feedback',
  },
} as const;

/**
 * Get all milestone types that should be checked for a given metric
 */
export function getMilestoneTypesForMetric(
  metricType: 'questions' | 'feedback'
): MilestoneType[] {
  return (Object.entries(MILESTONE_THRESHOLDS) as [MilestoneType, MilestoneThreshold][])
    .filter(([, threshold]) => threshold.type === metricType)
    .map(([type]) => type);
}

/**
 * Get milestone types that might be achieved given a current value
 */
export function getEligibleMilestones(
  metricType: 'questions' | 'feedback',
  currentValue: number
): MilestoneType[] {
  return getMilestoneTypesForMetric(metricType).filter(
    (type) => currentValue >= MILESTONE_THRESHOLDS[type].value
  );
}

/**
 * Get the display label for a milestone type
 */
export function getMilestoneLabel(milestoneType: MilestoneType): string {
  return MILESTONE_THRESHOLDS[milestoneType].label;
}

/**
 * Get the threshold value for a milestone type
 */
export function getMilestoneThresholdValue(milestoneType: MilestoneType): number {
  return MILESTONE_THRESHOLDS[milestoneType].value;
}
